import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { withUploadToken } from '../../utils/fileUrl';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import MedicationIcon from '@mui/icons-material/Medication';
import PaymentsIcon from '@mui/icons-material/Payments';
import PersonIcon from '@mui/icons-material/Person';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
} from '../../theme/smartStableTheme';
import { formatDateTimeDisplay } from '../../utils/dateUtils';

const apiUrl = process.env.REACT_APP_API_URL;

function formatDate(value) {
  return formatDateTimeDisplay(value);
}

function formatMoney(value) {
  const number = Number(value || 0);
  return `${number.toLocaleString('en-US', { maximumFractionDigits: 2 })} SAR`;
}

// Money visibility: System Admin (role_id=1) and Accountant (role_id=7) can
// see prices and totals. Clinical roles get them hidden — pricing is a
// finance concern, not a medical one.
function canViewPrices() {
  const roleId = Number(localStorage.getItem('role_id') || 0);
  return roleId === 1 || roleId === 7;
}

function approvalMeta(value, t) {
  const status = Number(value);
  if (status === 1) {
    return { label: t('approved') || 'Approved', color: COLORS.green, bg: COLORS.greenSoft };
  }
  if (status === 2) {
    return { label: t('rejected') || 'Rejected', color: COLORS.rose, bg: COLORS.roseSoft };
  }
  if (status === 3) {
    return { label: t('needs_edit') || 'Needs edit', color: COLORS.warning, bg: COLORS.warningSoft };
  }
  return { label: t('under_review') || 'Under review', color: COLORS.gold, bg: COLORS.goldSoft };
}

export default function RecordDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { record_id: recordId } = useParams();
  const [recordData, setRecordData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approvalSaving, setApprovalSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [systemInfo, setSystemInfo] = useState({ system_name: 'Smart Stable', logo: null });

  const isManager = Number(localStorage.getItem('role_id') || 0) === 1;

  // شعار المربط واسمه (للترويسة في التقرير الطبي).
  useEffect(() => {
    axios
      .get(`${apiUrl}/system-settings`)
      .then(({ data }) =>
        setSystemInfo({
          system_name: data?.system_name || 'Smart Stable',
          logo: data?.logo || null,
        })
      )
      .catch(() => {});
  }, []);

  const fetchRecordDetails = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${apiUrl}/vetclinic-records/${recordId}`);
      const nextRecord = { ...data };
      nextRecord.file_uploads = typeof nextRecord.file_uploads === 'string'
        ? nextRecord.file_uploads.split(';').filter(Boolean)
        : Array.isArray(nextRecord.file_uploads)
          ? nextRecord.file_uploads
          : [];

      if (nextRecord.owner_id) {
        try {
          const ownerResponse = await axios.get(`${apiUrl}/owners/${nextRecord.owner_id}`);
          const owner = ownerResponse.data || {};
          nextRecord.owner_name = `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || nextRecord.OwnerName;
          nextRecord.owner_phone = owner.phone || '-';
        } catch {
          nextRecord.owner_name = nextRecord.OwnerName || '-';
          nextRecord.owner_phone = '-';
        }
      } else {
        nextRecord.owner_name = nextRecord.OwnerName || '-';
        nextRecord.owner_phone = '-';
      }

      setRecordData(nextRecord);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to load record details');
    } finally {
      setLoading(false);
    }
  }, [recordId]);

  useEffect(() => {
    fetchRecordDetails();
  }, [fetchRecordDetails]);

  const images = useMemo(() => (
    recordData?.file_uploads?.length
      ? recordData.file_uploads.map((file) => ({
          original: withUploadToken(`${apiUrl}/uploads/${file}`),
          thumbnail: withUploadToken(`${apiUrl}/uploads/${file}`),
        }))
      : []
  ), [recordData]);

  const updateApproval = async (approval) => {
    if (!recordData || !isManager) return;
    setApprovalSaving(true);
    setError('');
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${apiUrl}/vetclinic-records/${recordData.record_id}/approval`,
        { approval },
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      );
      setRecordData((current) => ({ ...current, approval }));
      setMessage(t('saved') || 'Saved');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to update approval status');
    } finally {
      setApprovalSaving(false);
    }
  };

  // توليد تقرير طبي قابل للطباعة/الحفظ كـPDF بناءً على بيانات الصفحة.
  const handlePrintReport = () => {
    if (!recordData || !isManager) return; // التقرير للأدمن فقط
    const showPrices = canViewPrices();
    const esc = (v) =>
      String(v ?? '').replace(/[&<>"]/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])
      );
    const logoUrl = systemInfo.logo ? `${apiUrl}${systemInfo.logo}` : '';
    const dash = '—';
    const val = (v) => (v === 0 || v ? esc(v) : dash);

    const procRows = (recordData.procedures || []).length
      ? recordData.procedures
          .map(
            (p) =>
              `<tr><td>${val(p.procedure_name)}</td>${
                showPrices ? `<td class="num">${esc(formatMoney(p.price))}</td>` : ''
              }</tr>`
          )
          .join('')
      : `<tr><td colspan="${showPrices ? 2 : 1}" class="empty">${dash}</td></tr>`;

    const medRows = (recordData.medications || []).length
      ? recordData.medications
          .map(
            (m) =>
              `<tr><td>${val(m.medication_name)}</td><td>${val(m.dosage)}</td><td class="num">${val(
                m.quantity
              )}</td><td>${val(m.unit)}</td><td>${val(m.duration)}</td>${
                showPrices ? `<td class="num">${esc(formatMoney(m.price))}</td>` : ''
              }</tr>`
          )
          .join('')
      : `<tr><td colspan="${showPrices ? 6 : 5}" class="empty">${dash}</td></tr>`;

    const clinicalRow = (labelAr, labelEn, value) =>
      `<tr><th>${labelAr} / ${labelEn}</th><td>${val(value)}</td></tr>`;

    const html = `<!doctype html>
<html dir="rtl" lang="ar"><head><meta charset="utf-8">
<title>تقرير طبي #${esc(recordData.record_id)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; color: #222; margin: 0; padding: 24px; }
  .header { display: flex; align-items: center; justify-content: space-between; border-bottom: 3px solid #B79B5B; padding-bottom: 12px; margin-bottom: 18px; }
  .header .brand { display: flex; align-items: center; gap: 14px; }
  .header img { height: 70px; width: auto; object-fit: contain; }
  .header h1 { font-size: 22px; margin: 0; color: #252248; }
  .header .title { text-align: left; }
  .header .title h2 { margin: 0; font-size: 18px; color: #B79B5B; }
  .meta { display: flex; flex-wrap: wrap; gap: 8px 28px; margin-bottom: 14px; font-size: 13px; color: #444; }
  .meta b { color: #111; }
  h3 { font-size: 15px; color: #252248; border-right: 4px solid #B79B5B; padding-right: 8px; margin: 18px 0 8px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: right; vertical-align: top; }
  thead th { background: #f4f1ea; color: #252248; }
  .info th { background: #fafafa; width: 180px; }
  .num { text-align: center; white-space: nowrap; }
  .empty { text-align: center; color: #999; }
  .signature { margin-top: 40px; display: flex; justify-content: space-between; font-size: 13px; }
  .signature .box { text-align: center; }
  .signature .line { margin-top: 36px; border-top: 1px solid #555; padding-top: 4px; min-width: 220px; }
  .footer { margin-top: 28px; border-top: 1px solid #ddd; padding-top: 8px; font-size: 11px; color: #888; text-align: center; }
  @media print { body { padding: 0; } @page { margin: 14mm; } }
</style></head>
<body>
  <div class="header">
    <div class="brand">
      ${logoUrl ? `<img src="${esc(logoUrl)}" alt="logo" />` : ''}
      <h1>${esc(systemInfo.system_name)}</h1>
    </div>
    <div class="title">
      <h2>تقرير طبي بيطري</h2>
      <div>Veterinary Medical Report</div>
    </div>
  </div>

  <div class="meta">
    <span><b>رقم السجل / Record #:</b> ${esc(recordData.record_id)}</span>
    <span><b>التاريخ / Date:</b> ${esc(formatDate(recordData.record_date))}</span>
    <span><b>الطبيب المُنشئ / Doctor:</b> ${val(recordData.doctor_name)}</span>
  </div>

  <h3>بيانات الخيل والمالك / Horse & Owner</h3>
  <table class="info">
    <tr><th>اسم الخيل / Horse</th><td>${val(recordData.horse_name)}</td></tr>
    <tr><th>رقم الشريحة / RFID</th><td>${val(recordData.rfid_chip_number)}</td></tr>
    <tr><th>المالك / Owner</th><td>${val(recordData.owner_name)}</td></tr>
    <tr><th>هاتف المالك / Phone</th><td>${val(recordData.owner_phone)}</td></tr>
  </table>

  <h3>الإجراءات / Procedures</h3>
  <table>
    <thead><tr><th>الإجراء / Procedure</th>${showPrices ? '<th class="num">السعر / Price</th>' : ''}</tr></thead>
    <tbody>${procRows}</tbody>
  </table>

  <h3>الأدوية / Medications</h3>
  <table>
    <thead><tr>
      <th>الدواء / Medication</th><th>الجرعة / Dosage</th><th class="num">الكمية / Qty</th>
      <th>الوحدة / Unit</th><th>المدة / Duration</th>${showPrices ? '<th class="num">السعر / Price</th>' : ''}
    </tr></thead>
    <tbody>${medRows}</tbody>
  </table>

  <h3>التفاصيل السريرية / Clinical Details</h3>
  <table class="info">
    ${clinicalRow('التاريخ المرضي', 'Medical history', recordData.medical_history)}
    ${clinicalRow('التشخيص', 'Diagnosis', recordData.diagnosis)}
    ${clinicalRow('العلاج', 'Treatment', recordData.treatment)}
    ${clinicalRow('ملاحظات', 'Notes', recordData.notes)}
    ${showPrices ? clinicalRow('الإجمالي', 'Total', formatMoney(recordData.total_amount)) : ''}
  </table>

  <div class="signature">
    <div class="box"><div class="line">${val(recordData.doctor_name)}<br/>الطبيب المُنشئ للتقرير / Reporting Doctor</div></div>
    <div class="box"><div class="line">${esc(systemInfo.system_name)}<br/>الختم / Stamp</div></div>
  </div>

  <div class="footer">
    تم إنشاء هذا التقرير من نظام ${esc(systemInfo.system_name)} بتاريخ ${esc(new Date().toLocaleString('en-GB'))}
  </div>

  <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 350); };</script>
</body></html>`;

    const win = window.open('', '_blank');
    if (!win) {
      setError(t('popup_blocked') || 'Please allow pop-ups to download the report.');
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh', bgcolor: COLORS.page }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!recordData) {
    return (
      <Box className="smart-page-container" sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
        <Alert severity="error">{error || t('no_data') || 'No data available'}</Alert>
      </Box>
    );
  }

  const approval = approvalMeta(recordData.approval, t);
  const proceduresTotal = (recordData.procedures || []).reduce((sum, item) => sum + Number(item.price || 0), 0);
  const medicationsTotal = (recordData.medications || []).reduce((sum, item) => sum + Number(item.price || 0), 0);

  return (
    <Box className="smart-page-container" sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 2.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ width: 64, height: 64, bgcolor: COLORS.blueSoft, color: COLORS.blue }}>
              <HealthAndSafetyIcon />
            </Avatar>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
                  {t('clinic_record_details') || 'Clinic record details'}
                </Typography>
                <Chip label={approval.label} sx={{ bgcolor: approval.bg, color: approval.color, fontWeight: 900 }} />
              </Stack>
              <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>
                #{recordData.record_id} · <span className="date-ltr">{formatDate(recordData.record_date)}</span>
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/recordlist')} sx={{ color: COLORS.text, fontWeight: 900 }}>
              {t('back') || 'Back'}
            </Button>
            <Button
              startIcon={<EditIcon />}
              variant="outlined"
              onClick={() => navigate(`/VetClinicRecordEdit/${recordData.record_id}`)}
              sx={{ borderColor: COLORS.line, color: COLORS.gold, fontWeight: 900 }}
            >
              {t('edit') || 'Edit'}
            </Button>
            {isManager && (
              <Button
                startIcon={<PrintIcon />}
                variant="contained"
                onClick={handlePrintReport}
                sx={{ bgcolor: COLORS.gold, color: '#fff', fontWeight: 900, '&:hover': { bgcolor: COLORS.goldDark || COLORS.gold } }}
              >
                {t('download_medical_report') || 'Download report'}
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      {isManager && (
        <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 2.5, borderColor: approval.color }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2}>
            <Box>
              <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
                {t('approval_actions') || 'Approval actions'}
              </Typography>
              <Typography sx={{ color: COLORS.muted }}>
                {t('approval_actions_hint') || 'Manager-only controls for sending the record to accounting and reports.'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button disabled={approvalSaving || Number(recordData.approval) === 1} startIcon={<CheckCircleIcon />} onClick={() => updateApproval(1)} sx={{ color: COLORS.green, fontWeight: 900 }}>
                {t('approve') || 'Approve'}
              </Button>
              <Button disabled={approvalSaving || Number(recordData.approval) === 3} startIcon={<AutorenewRoundedIcon />} onClick={() => updateApproval(3)} sx={{ color: COLORS.warning, fontWeight: 900 }}>
                {t('request_edit') || 'Request edit'}
              </Button>
              <Button disabled={approvalSaving || Number(recordData.approval) === 2} startIcon={<CancelIcon />} onClick={() => updateApproval(2)} sx={{ color: COLORS.rose, fontWeight: 900 }}>
                {t('reject') || 'Reject'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      <Grid container spacing={2.5}>
        {/* When the user can't see prices, the 3 remaining metrics expand
            to md=4 each so the row stays balanced visually. */}
        <Grid item xs={12} md={canViewPrices() ? 3 : 4}>
          <Metric icon={<ReceiptLongIcon />} label={t('record_id') || 'Record ID'} value={`#${recordData.record_id}`} helper={<span className="date-ltr">{formatDate(recordData.record_date)}</span>} />
        </Grid>
        <Grid item xs={12} md={canViewPrices() ? 3 : 4}>
          <Metric icon={<HealthAndSafetyIcon />} label={t('horse_name') || 'Horse'} value={recordData.horse_name || '-'} helper={`RFID ${recordData.rfid_chip_number || '-'}`} color={COLORS.blue} soft={COLORS.blueSoft} />
        </Grid>
        <Grid item xs={12} md={canViewPrices() ? 3 : 4}>
          <Metric icon={<PersonIcon />} label={t('owner_name') || 'Owner'} value={recordData.owner_name || '-'} helper={recordData.owner_phone || '-'} color={COLORS.green} soft={COLORS.greenSoft} />
        </Grid>
        {canViewPrices() && (
          <Grid item xs={12} md={3}>
            <Metric icon={<PaymentsIcon />} label={t('total_amount') || 'Total amount'} value={formatMoney(recordData.total_amount)} helper={recordData.payment_type_name || '-'} color={COLORS.gold} soft={COLORS.goldSoft} />
          </Grid>
        )}

        <Grid item xs={12} md={7}>
          <Section title={t('medical_summary') || 'Medical summary'}>
            <InfoBlock title={t('medical_history') || 'Medical history'} value={recordData.medical_history} />
            <InfoBlock title={t('diagnosis') || 'Diagnosis'} value={recordData.diagnosis} />
            <InfoBlock title={t('treatment') || 'Treatment'} value={recordData.treatment} />
            <InfoBlock title={t('notes') || 'Notes'} value={recordData.notes} />
          </Section>
        </Grid>

        <Grid item xs={12} md={5}>
          <Section title={t('clinical_services') || 'Clinical services'}>
            <ListBlock
              title={t('procedures') || 'Procedures'}
              total={proceduresTotal}
              rows={recordData.procedures}
              getName={(row) => row.procedure_name}
              getMeta={(row) => (canViewPrices() ? formatMoney(row.price) : '')}
              empty={t('no_procedures_selected') || 'No procedures selected'}
              hideTotal={!canViewPrices()}
            />
            <Divider sx={{ my: 2, borderColor: COLORS.line }} />
            <ListBlock
              title={t('medications') || 'Medications'}
              total={medicationsTotal}
              rows={recordData.medications}
              getName={(row) => row.medication_name}
              getMeta={(row) => {
                const parts = [
                  `${row.dosage || '-'} ${row.unit || ''}`,
                  `${row.quantity || 0}`,
                  row.duration ? `${row.duration}` : null,
                  canViewPrices() ? formatMoney(row.price) : null,
                ].filter(Boolean);
                return parts.join(' · ');
              }}
              empty={t('no_medications_provided') || 'No medications provided'}
              icon={<MedicationIcon />}
              hideTotal={!canViewPrices()}
            />
          </Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title={t('doctor') || 'Doctor'}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: COLORS.goldSoft, color: COLORS.gold }}>
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{recordData.doctor_name || '-'}</Typography>
                <Typography variant="caption" sx={{ color: COLORS.muted }}>{t('responsible_user') || 'Responsible user'}</Typography>
              </Box>
            </Stack>
          </Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title={t('attachments') || 'Attachments'}>
            {images.length ? (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {images.map((image, index) => (
                  <Tooltip title={t('view') || 'View'} key={image.thumbnail}>
                    <Box
                      component="img"
                      src={image.thumbnail}
                      alt={`Uploaded ${index + 1}`}
                      onClick={() => {
                        setPhotoIndex(index);
                        setIsOpen(true);
                      }}
                      sx={{
                        width: 96,
                        height: 96,
                        objectFit: 'cover',
                        border: `1px solid ${COLORS.line}`,
                        cursor: 'pointer',
                      }}
                    />
                  </Tooltip>
                ))}
              </Stack>
            ) : (
              <Typography sx={{ color: COLORS.muted }}>{t('no_attachments') || 'No attachments'}</Typography>
            )}
          </Section>
        </Grid>
      </Grid>

      <ImageLightbox
        open={isOpen && images.length > 0}
        images={images}
        index={photoIndex}
        onClose={() => setIsOpen(false)}
        onIndexChange={setPhotoIndex}
        altText={t('image_preview')}
      />
    </Box>
  );
}

function ImageLightbox({ open, images, index, onClose, onIndexChange, altText }) {
  if (!open || !images?.length) return null;

  const safeIndex = ((index % images.length) + images.length) % images.length;
  const showPrev = () => onIndexChange((safeIndex + images.length - 1) % images.length);
  const showNext = () => onIndexChange((safeIndex + 1) % images.length);
  const hasMultiple = images.length > 1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      onKeyDown={(e) => {
        if (!hasMultiple) return;
        if (e.key === 'ArrowLeft') showPrev();
        if (e.key === 'ArrowRight') showNext();
      }}
      PaperProps={{
        sx: { bgcolor: 'rgba(0,0,0,0.92)', boxShadow: 'none', position: 'relative', m: { xs: 1, md: 2 } },
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="close"
        sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', zIndex: 3, bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
      >
        <CloseIcon />
      </IconButton>

      {hasMultiple && (
        <IconButton
          onClick={showPrev}
          aria-label="previous"
          sx={{ position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)', color: '#fff', zIndex: 3, bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
        >
          <ChevronLeftIcon />
        </IconButton>
      )}

      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '70vh', p: 2 }}>
        <Box
          component="img"
          src={images[safeIndex]?.original}
          alt={altText}
          sx={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', userSelect: 'none' }}
        />
      </Box>

      {hasMultiple && (
        <IconButton
          onClick={showNext}
          aria-label="next"
          sx={{ position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)', color: '#fff', zIndex: 3, bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}

      {hasMultiple && (
        <Typography
          sx={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', color: '#fff', fontWeight: 700, bgcolor: 'rgba(0,0,0,0.5)', px: 1.5, py: 0.3, borderRadius: 1 }}
        >
          {safeIndex + 1} / {images.length}
        </Typography>
      )}
    </Dialog>
  );
}

function Metric({ icon, label, value, helper, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 120 }}>
      <Stack direction="row" spacing={1.4} alignItems="center">
        <Avatar sx={{ bgcolor: soft, color }}>{icon}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color, fontWeight: 900 }}>{label}</Typography>
          <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900, overflowWrap: 'anywhere' }}>{value}</Typography>
          <Typography variant="caption" sx={{ color: COLORS.muted }}>{helper}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function Section({ title, children }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, height: '100%' }}>
      <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900, mb: 1.5 }}>{title}</Typography>
      {children}
    </Paper>
  );
}

function InfoBlock({ title, value }) {
  return (
    <Box sx={{ p: 1.5, border: `1px solid ${COLORS.line}`, bgcolor: COLORS.cardSoft, mb: 1.2 }}>
      <Typography variant="caption" sx={{ color: COLORS.gold, fontWeight: 900 }}>{title}</Typography>
      <Typography sx={{ color: COLORS.text, whiteSpace: 'pre-wrap' }}>{value || '-'}</Typography>
    </Box>
  );
}

function ListBlock({ title, total, rows, getName, getMeta, empty, icon = <HealthAndSafetyIcon />, hideTotal = false }) {
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar sx={{ width: 32, height: 32, bgcolor: COLORS.blueSoft, color: COLORS.blue }}>{icon}</Avatar>
          <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{title}</Typography>
        </Stack>
        {/* When prices are hidden show the row count instead of the total
            money — keeps the chip useful without leaking pricing data. */}
        {hideTotal ? (
          <Chip
            size="small"
            label={`${rows?.length || 0}`}
            sx={{ bgcolor: COLORS.blueSoft, fontWeight: 900 }}
          />
        ) : (
          <Chip size="small" label={formatMoney(total)} sx={{ bgcolor: COLORS.goldSoft, fontWeight: 900 }} />
        )}
      </Stack>
      <Stack spacing={1}>
        {rows?.length ? rows.map((row, index) => (
          <Box key={`${getName(row)}-${index}`} sx={{ p: 1, border: `1px solid ${COLORS.line}`, bgcolor: COLORS.card }}>
            <Stack direction="row" justifyContent="space-between" gap={1}>
              <Typography sx={{ color: COLORS.text, fontWeight: 800 }}>{getName(row) || '-'}</Typography>
              <Typography variant="caption" sx={{ color: COLORS.muted }}>{getMeta(row)}</Typography>
            </Stack>
          </Box>
        )) : <Typography sx={{ color: COLORS.muted }}>{empty}</Typography>}
      </Stack>
    </Box>
  );
}
